import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';
import CustomerSelector from './CustomerSelector';
import 'react-datepicker/dist/react-datepicker.css';

const { FiPlus, FiTrash2, FiSave, FiDollarSign, FiHash, FiMapPin, FiCalendar, FiArrowLeft, FiCheckSquare } = FiIcons;

const TravelInvoiceEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { invoices, updateInvoice, settings } = useInvoice();
  const existingInvoice = invoices.find(inv => inv.id === parseInt(id));

  const [formData, setFormData] = useState({
    customer: {
      name: '',
      address: '',
      phone: '',
      email: ''
    },
    items: [
      {
        name: '',
        description: '',
        quantity: 1,
        price: 0,
        total: 0
      }
    ],
    travelDate: new Date(),
    returnDate: null,
    destination: '',
    currency: 'USD',
    processingFee: 0,
    notes: '',
    number: 1,
    invoiceType: 'travel',
    status: 'unpaid'
  });

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' }
  ];

  useEffect(() => {
    if (existingInvoice) {
      setFormData({
        ...existingInvoice,
        travelDate: new Date(existingInvoice.travelDate),
        returnDate: existingInvoice.returnDate ? new Date(existingInvoice.returnDate) : null,
        number: existingInvoice.number,
        status: existingInvoice.status || 'unpaid'
      });
    }
  }, [existingInvoice]);

  if (!existingInvoice) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Invoice not found</h3>
            <p className="text-gray-600">The requested invoice could not be found.</p>
            <button
              onClick={() => navigate('/invoices')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Invoices
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getCurrencySymbol = (code) => {
    return currencies.find(c => c.code === code)?.symbol || '$';
  };

  const handleCustomerChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customer: { ...prev.customer, [field]: value }
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = newItems[index].quantity * newItems[index].price;
    }
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          name: '',
          description: '',
          quantity: 1,
          price: 0,
          total: 0
        }
      ]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal + formData.processingFee;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const subtotal = calculateSubtotal();
    const total = calculateTotal();

    const updatedInvoice = {
      ...formData,
      subtotal,
      tax: 0,
      total,
      companyInfo: settings.travelAgencyInfo
    };

    updateInvoice(existingInvoice.id, updatedInvoice);
    alert('Travel invoice updated successfully!');
    navigate('/invoices');
  };

  const subtotal = calculateSubtotal();
  const total = calculateTotal();

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/invoices')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
          Back to Invoices
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Travel Invoice</h1>
      </div>

      <motion.form
        onSubmit={handleSubmit}
        className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-lg overflow-hidden border-2 border-blue-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Invoice Number</h3>
              <div className="flex items-center">
                <div className="relative w-full max-w-xs">
                  <SafeIcon icon={FiHash} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={formData.number}
                    onChange={(e) => setFormData(prev => ({ ...prev, number: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Invoice Number"
                  />
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Payment Status</h3>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.status === 'paid'}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'paid' : 'unpaid' }))}
                    className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <SafeIcon icon={FiCheckSquare} className="w-5 h-5 ml-3 mr-2 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Mark Invoice as Paid
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Customer Information</h3>
            <CustomerSelector
              customer={formData.customer}
              onChange={handleCustomerChange}
            />
          </div>

          {/* Travel Details */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Travel Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMapPin} className="inline w-4 h-4 mr-1" />
                  Destination
                </label>
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Paris, France"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiCalendar} className="inline w-4 h-4 mr-1" />
                  Travel Date
                </label>
                <DatePicker
                  selected={formData.travelDate}
                  onChange={(date) => setFormData(prev => ({ ...prev, travelDate: date }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  dateFormat="MMMM dd, yyyy"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiCalendar} className="inline w-4 h-4 mr-1" />
                  Return Date (Optional)
                </label>
                <DatePicker
                  selected={formData.returnDate}
                  onChange={(date) => setFormData(prev => ({ ...prev, returnDate: date }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  dateFormat="MMMM dd, yyyy"
                  minDate={formData.travelDate}
                />
              </div>
            </div>
          </div>

          {/* Currency Selection */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Currency</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {currencies.map((currency) => (
                <motion.label
                  key={currency.code}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.currency === currency.code
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <input
                    type="radio"
                    name="currency"
                    value={currency.code}
                    checked={formData.currency === currency.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <span className="text-2xl font-bold mr-3">{currency.symbol}</span>
                    <div>
                      <div className="font-medium text-gray-900">{currency.code}</div>
                      <div className="text-sm text-gray-500">{currency.name}</div>
                    </div>
                  </div>
                </motion.label>
              ))}
            </div>
          </div>

          {/* Services/Items */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Services & Packages</h3>
              <motion.button
                type="button"
                onClick={addItem}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Add Service
              </motion.button>
            </div>
            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-4 rounded-lg border border-blue-200"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">Service {index + 1}</h4>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Name
                      </label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Flight Tickets, Hotel Accommodation"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Service details..."
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantity
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value) || 1)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price ({getCurrencySymbol(formData.currency)})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, 'price', Number(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Total
                        </label>
                        <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 font-medium">
                          {getCurrencySymbol(formData.currency)}{item.total.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Processing Fee */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Additional Fees</h3>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Service Fee:</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.processingFee}
                onChange={(e) => setFormData(prev => ({ ...prev, processingFee: Number(e.target.value) || 0 }))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-sm text-gray-500">{getCurrencySymbol(formData.currency)}</span>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Additional Notes</h3>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Any special requests or additional information..."
            />
          </div>

          {/* Invoice Summary */}
          <div className="bg-gradient-to-br from-blue-100 to-cyan-100 p-6 rounded-lg mb-8 border border-blue-300">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Invoice Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">Subtotal:</span>
                <span className="font-medium">{getCurrencySymbol(formData.currency)}{subtotal.toFixed(2)}</span>
              </div>
              {formData.processingFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-700">Service Fee:</span>
                  <span className="font-medium">{getCurrencySymbol(formData.currency)}{formData.processingFee.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-blue-300 pt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-blue-600">{getCurrencySymbol(formData.currency)}{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-green-700 hover:to-teal-700 transition-all duration-300 flex items-center justify-center shadow-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
            Update Travel Invoice
          </motion.button>
        </div>
      </motion.form>
    </div>
  );
};

export default TravelInvoiceEdit;